import { useEffect, useState } from 'react';
import {
	Box,
	Chip,
	CircularProgress,
	DialogContent,
	Grid,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import { RequestQuoteOutlined } from '@mui/icons-material';
import {
	faCalendarAlt,
	faFilePdf,
	faHashtag,
	faPerson,
	faUser,
	faUserTie,
	faStore,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from '../../components/MoleDialogShell';
import DocumentExportDialog, { DocumentExportLayout, DocumentImageSize } from '../../components/DocumentExportDialog';
import { useAuth } from '../../hooks/useAuth';
import { canAmend, canExportPdf } from '../../util/access';
import { useUI } from '../../providers/MoleUIContext';
import { useAppSocket } from '../../providers/AppSocketContext';
import { toast } from 'react-toastify';
import { Util } from '../../util/Util';
import ContactDetailsPanel, { hasContactDetails } from '../client/ContactDetailsPanel';
import { offerKindI18nKey, offerStatusI18nKey, type OfferRow } from './offerTypes';

import { addMoney, lineTotal } from "../../util/quantity";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle: OfferRow | null;
}

const OfferReport = ({ isOpen, toggle, bundle }: Props) => {
	const [record, setRecord] = useState<any>(null);
	const [exportOpen, setExportOpen] = useState(false);
	const { api, access } = useAuth();
	const { queueReRun, setLoadingMessage, t } = useUI();
	const { appSettings } = useAppSocket();
	const themeColor = useMoleThemeColor();
	const batch = bundle?.batch;
	const showAgents = Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_AGENTS?.value);
	const showItemCode = Util.isBoolSettingEnabled(appSettings?.UI_SHOW_ITEM_CODE?.value);

	const fetchData = async () => {
		if (!batch) return;
		const res = await api.get(`/offers/${batch}`);
		if (res?.status === 200) {
			setRecord(res.data);
			return;
		}
		if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchData);
			return;
		}
		if (res?.status === 404) {
			toast.warn(t('common:toast.warn.noRecord'));
		}
		toggle();
	};

	const handlePrint = async (
		layout: DocumentExportLayout = 'plain',
		imageSize: DocumentImageSize = 'm',
	) => {
		if (!batch || !canExportPdf(access, 'offer')) return;
		setLoadingMessage('EXPORT');
		const params = new URLSearchParams();
		if (layout === 'images') {
			params.set('with_images', '1');
			params.set('image_size', imageSize);
		}
		const query = params.toString() ? `?${params.toString()}` : '';
		const res = await api.get(`/reports/offers/${batch}${query}`);
		if (res?.status === 200 && res.data?.data) {
			const pdfData = atob(res.data.data);
			const blob = new Blob([Uint8Array.from(pdfData, (c) => c.charCodeAt(0))], {
				type: 'application/pdf',
			});
			window.open(URL.createObjectURL(blob));
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(() => handlePrint(layout, imageSize));
		} else {
			toast.error(t('common:report.failedToGenerate'));
		}
		setLoadingMessage(null);
	};

	useEffect(() => {
		if (!isOpen) {
			setExportOpen(false);
			return;
		}
		if (batch) {
			setRecord(null);
			void fetchData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, batch]);

	const details = record?.details || {};
	const lines = record?.records || [];
	const kind = String(details.kind || bundle?.kind || 'QUOTE');
	const status = String(details.status || bundle?.status || 'OPEN');
	const isDelivery = kind.toUpperCase() === 'DELIVERY';
	const grandTotal = lines.reduce(
		(sum: number, row: any) =>
			addMoney(sum, row.total || lineTotal(row.planned_qty || row.quantity, row.amount)),
		0,
	);
	const statusColor = status === 'CONVERTED' ? 'success' : status === 'CANCELLED' ? 'default' : 'info';

	return (
		<MoleDialog
			open={isOpen}
			onClose={toggle}
			maxWidth="md"
			fullWidth
			themeColor={themeColor}
			paperOptions={{ maxHeight: '95vh' }}
		>
			<MoleDialogHeader
				eyebrow={t(offerKindI18nKey(kind))}
				title={details.reference || bundle?.reference || t('common:status.loading')}
				subtitle={
					<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
						<Chip size="small" color={statusColor as any} label={t(offerStatusI18nKey(status))} />
					</Stack>
				}
				icon={<RequestQuoteOutlined />}
				onClose={toggle}
				themeColor={themeColor}
			/>

			<DialogContent sx={{ p: 4 }}>
				{record ? (
					<Box>
						<Grid container spacing={3} sx={{ mb: 3 }}>
							<Grid size={{ xs: 12, sm: isDelivery ? 12 : 6 }}>
								<Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800 }}>
									{t('common:offer.details')}
								</Typography>
								<Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'background.default', borderStyle: 'dashed' }}>
									<Stack spacing={1.5}>
										<DetailItem icon={faHashtag} label={t('common:tables.reference')} value={details.reference} />
										<DetailItem icon={faPerson} label={t('common:tables.client')} value={details.client} />
										{details.depot && (
											<DetailItem icon={faStore} label={t('common:tables.depot')} value={details.depot} />
										)}
										{showAgents && details.agent && (
											<DetailItem icon={faUserTie} label={t('common:tables.agent')} value={details.agent} />
										)}
										<DetailItem icon={faCalendarAlt} label={t('common:tables.date')} value={Util.formatDate(details.date, 'full')} />
										{details.valid_until && (
											<DetailItem icon={faCalendarAlt} label={t('common:offer.validUntil')} value={Util.formatDate(details.valid_until, 'full')} />
										)}
										<DetailItem icon={faUser} label={t('common:report.recordedBy')} value={details.user} />
									</Stack>
								</Paper>
							</Grid>
							{hasContactDetails(details) && (
								<Grid size={{ xs: 12, sm: 6 }}>
									<ContactDetailsPanel details={details} />
								</Grid>
							)}
							{!isDelivery && (
								<Grid size={{ xs: 12, sm: 6 }}>
									<Paper
										variant="outlined"
										sx={{
											p: 3,
											mt: { xs: 0, sm: 4 },
											borderRadius: 3,
											bgcolor: 'grey.900',
											color: 'white',
										}}
									>
										<Stack direction="row" justifyContent="space-between" alignItems="center">
											<Typography variant="h6" fontWeight={400}>{t('common:report.grandTotal')}</Typography>
											<Typography variant="h5" fontWeight={900} sx={{ color: themeColor }}>
												{Util.formatNumber(grandTotal)}
											</Typography>
										</Stack>
									</Paper>
								</Grid>
							)}
						</Grid>

						<TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
							<Table size="small">
								<TableHead sx={{ bgcolor: 'action.hover' }}>
									<TableRow>
										<TableCell sx={{ fontWeight: 700 }}>{t('common:tables.item')}</TableCell>
										<TableCell align="right" sx={{ fontWeight: 700 }}>{t('common:tables.quantity')}</TableCell>
										{!isDelivery && (
											<>
												<TableCell align="right" sx={{ fontWeight: 700 }}>{t('common:tables.sellingPrice')}</TableCell>
												<TableCell align="right" sx={{ fontWeight: 700 }}>{t('common:tables.lineTotal')}</TableCell>
											</>
										)}
									</TableRow>
								</TableHead>
								<TableBody>
									{lines.map((item: any, idx: number) => (
										<TableRow key={item.item_id || idx} hover>
											<TableCell sx={{ py: 1.5 }}>
												<Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
												{showItemCode && (
													<Typography variant="caption" color="text.secondary">
														{t('common:report.code', { code: item.item_code || t('common:ui.placeholders.notAvailable') })}
													</Typography>
												)}
											</TableCell>
											<TableCell align="right">{Util.formatNumber(item.planned_qty || item.quantity)}</TableCell>
											{!isDelivery && (
												<>
													<TableCell align="right">{Util.formatNumber(item.amount)}</TableCell>
													<TableCell align="right" sx={{ fontWeight: 700 }}>
														{Util.formatNumber(item.total || lineTotal(item.planned_qty || item.quantity, item.amount))}
													</TableCell>
												</>
											)}
										</TableRow>
									))}
									{lines.length === 0 && (
										<TableRow>
											<TableCell colSpan={isDelivery ? 2 : 4}>
												<Typography color="text.secondary">{t('common:offer.noItems')}</Typography>
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>

						{details.description && (
							<Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', borderLeft: '4px solid', borderColor: 'info.main' }}>
								<Typography variant="caption" fontWeight={800} color="info.main" sx={{ textTransform: 'uppercase' }}>
									{t('common:taskPane.notes')}
								</Typography>
								<Typography variant="body2" sx={{ mt: 0.5 }}>{details.description}</Typography>
							</Box>
						)}
					</Box>
				) : (
					<Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
						<CircularProgress thickness={5} size={48} sx={{ color: themeColor }} />
						<Typography sx={{ mt: 2, color: 'text.secondary' }}>{t('common:report.loadingReport')}</Typography>
					</Stack>
				)}
			</DialogContent>

			<MoleDialogActions>
				<MoleCancelButton onClick={toggle}>{t('common:ui.buttons.close')}</MoleCancelButton>
				{canExportPdf(access, 'offer') && (
					<MolePrimaryButton
						startIcon={<FontAwesomeIcon icon={faFilePdf} />}
						onClick={() => setExportOpen(true)}
						disabled={!record}
					>
						{t('common:ui.buttons.exportAsPdf')}
					</MolePrimaryButton>
				)}
			</MoleDialogActions>

			<DocumentExportDialog
				open={exportOpen}
				onClose={() => setExportOpen(false)}
				onExport={(layout, imageSize) => { void handlePrint(layout, imageSize); }}
				document={batch ? { kind: 'offer', batch } : undefined}
				canEditImages={canAmend(access, 'offer')}
			/>
		</MoleDialog>
	);
};

const DetailItem = ({ icon, label, value }: { icon: any; label: string; value: string }) => (
	<Stack direction="row" spacing={1.5} alignItems="center">
		<FontAwesomeIcon icon={icon} style={{ width: 14, color: '#64748b' }} />
		<Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>{label}:</Typography>
		<Typography variant="body2" fontWeight={600}>{value || '—'}</Typography>
	</Stack>
);

export default OfferReport;
